import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import styles from './ai-prompt-box-input-field.module.scss';
import type {AsyncVoidFunction} from '@Utils/general.util';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {hasActiveAttachmentLoaders, isAnyAttachmentUploading} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {motion} from 'framer-motion';
import {isFileDuplicateBySignature} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {handlePosterItemsPaste} from '@Components/ai-prompt-box/libraries/ai-prompt-box-poster-items-paste.library';
import {extractImageFilesFromClipboard} from '@Utils/clipboard.util';
import {useAiPromptBoxDragDropUploader} from '@Components/ai-prompt-box/hooks/useAiPromptBoxDragDropUploader';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {DAILY_LIMIT_ERROR_VISIBLE_MS} from './ai-prompt-box-input-field.types';

interface PromptInputFieldProps {
  id: string;
  handleSubmission: AsyncVoidFunction;
  focus?: boolean;
  onFocus?: () => void;
  inputClasses?: string;
  allowSoftSendDuringUploads?: boolean;
  inputTestId?: string;
}

export function AiPromptBoxInputField(props: PromptInputFieldProps): ReactElement {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldFocusRef = useRef<boolean>(props.focus ?? false);
  const {bottomShadow, topShadow} = useTopBottomScrollShadow(textAreaRef, true);
  const MAX_PROMPT_LENGTH = 2000;
  const {state, actions} = useAiPromptBox(props.id);
  const isPromptBoxSelected = state.promptBoxState === PromptBoxState.ACTIVE;
  const isSoftSending = state.isFinishingUploads ?? false;
  const isDisabled =
    state.promptBoxState === PromptBoxState.LOADING ||
    state.promptBoxState === PromptBoxState.STOPPABLE_LOADING ||
    state.promptBoxState === PromptBoxState.DISABLED ||
    isSoftSending;
  const isAttachmentBlockingSubmit = props.allowSoftSendDuringUploads === false ? isAnyAttachmentUploading(state) : hasActiveAttachmentLoaders(state.attachmentLoaders);
  const isSubmissionDisabled = isDisabled || isAttachmentBlockingSubmit;
  const isTurnActive = state.promptBoxState === PromptBoxState.LOADING || state.promptBoxState === PromptBoxState.STOPPABLE_LOADING || isSoftSending;
  const placeholderText = isTurnActive ? '' : state.placeholder;
  const previouslyUploadedFilesRef = useRef<File[]>([]);
  const dailyLimitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {handleFiles: handlePastedFiles, handleReferenceImageFiles} = useAiPromptBoxDragDropUploader(props.id);

  useEffect(() => {
    return (): void => {
      if (dailyLimitTimeoutRef.current) {
        clearTimeout(dailyLimitTimeoutRef.current);
        dailyLimitTimeoutRef.current = null;
      }
    };
  }, []);

  const sizeClassMap = {
    [AiPromptBoxSize.SMALL]: styles.small,
    [AiPromptBoxSize.MEDIUM]: styles.medium,
    [AiPromptBoxSize.SINGLE_LINE]: styles.singleLine,
    [AiPromptBoxSize.SMALL_EXTENDED]: styles.small,
  };

  const adjustTextAreaHeight = (): void => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextAreaHeight();
  }, []);

  useDidUpdateEffect(() => {
    adjustTextAreaHeight();
  }, [state.prompt]);

  useEffect(() => {
    if (props.focus && shouldFocusRef.current && textAreaRef.current && !isDisabled && isPromptBoxSelected) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
    }
  }, [props.focus, isDisabled, isPromptBoxSelected]);

  useEffect(() => {
    if (isPromptBoxSelected && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
    }
  }, [isPromptBoxSelected]);

  useEffect(() => {
    if (state.focusKey !== undefined && !isDisabled && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
    }
  }, [state.focusKey, isDisabled]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const input = e.target.value;
    actions.update({prompt: input});
  };

  /** Flashes the daily-limit message for a few seconds, restarting the timer on a repeat press. */
  const flashDailyLimitError = (): void => {
    actions.update({showDailyLimitError: true});

    if (dailyLimitTimeoutRef.current) {
      clearTimeout(dailyLimitTimeoutRef.current);
    }

    dailyLimitTimeoutRef.current = setTimeout(() => {
      actions.update({showDailyLimitError: false});
      dailyLimitTimeoutRef.current = null;
    }, DAILY_LIMIT_ERROR_VISIBLE_MS);
  };

  const submitOnEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || window.PMW.isMobileOS() || isSubmissionDisabled) {
      return;
    }

    event.preventDefault();

    if (state.isDailyLimitReached) {
      flashDailyLimitError();

      return;
    }

    void props.handleSubmission();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    submitOnEnter(event);
  };

  const getTopShadowElement = (): ReactElement | null => {
    if (!topShadow) {
      return null;
    }

    return <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.2}} exit={{opacity: 0}} className={styles.topShadow} />;
  };

  const onBlur = (): void => {
    if (props.focus) {
      shouldFocusRef.current = true;
    }
  };

  const onFocus = (): void => {
    shouldFocusRef.current = false;
    props.onFocus?.();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    if (state.attachments === undefined) {
      return;
    }

    const didHandlePosterItemsPaste = handlePosterItemsPaste(e, {
      pasteAsReferenceImages: state.attachments.pasteAsReferenceImages ?? false,
      onReferenceImageFiles: handleReferenceImageFiles,
      onAttachImages: actions.addAttachments,
    });
    if (didHandlePosterItemsPaste) {
      return;
    }

    const imageFiles = extractImageFilesFromClipboard(e.clipboardData?.items);
    if (imageFiles.length === 0) {
      return;
    }

    e.preventDefault();

    const filesToUpload = imageFiles.filter((file) => !isFileDuplicateBySignature(file, previouslyUploadedFilesRef.current));
    if (filesToUpload.length === 0) {
      return;
    }

    previouslyUploadedFilesRef.current.push(...filesToUpload);
    handlePastedFiles(filesToUpload);
  };

  const textAreaRowsMap = {
    [AiPromptBoxSize.SINGLE_LINE]: 1,
    [AiPromptBoxSize.SMALL]: 1,
    [AiPromptBoxSize.MEDIUM]: 2,
    [AiPromptBoxSize.SMALL_EXTENDED]: 2,
  };

  const getInputFieldRows = (): number => {
    return textAreaRowsMap[state.size];
  };

  const getInputFieldClasses = (): string => {
    return `${styles.inputField} _full-width ${state.input?.hideScrollbar ? styles.hideScrollbar : ''} ${bottomShadow && state.prompt ? styles.bottomShadow : ''} ${sizeClassMap[state.size]} ${props.inputClasses ?? ''}`;
  };

  const getInputField = (): ReactElement => {
    return (
      <div className="_position-relative">
        {getTopShadowElement()}
        <textarea
          ref={textAreaRef}
          data-testid={props.inputTestId}
          className={getInputFieldClasses()}
          value={state.prompt}
          placeholder={placeholderText}
          onChange={handlePromptChange}
          onKeyDown={handleKeyDown}
          maxLength={MAX_PROMPT_LENGTH}
          onBlur={onBlur}
          onFocus={onFocus}
          onPaste={handlePaste}
          rows={getInputFieldRows()}
          disabled={isSoftSending}
        />
      </div>
    );
  };

  return getInputField();
}
